/**
 * ⛔⛔ LINKING A PATIENT RECORD TO ITS CRM CONTACT — Card P100, Release 1.0.
 *
 * One component, both directions, because it is one decision: *is this chart
 * and this contact the same person?* The doctor answers it from whichever
 * screen they happen to be on.
 *
 * ⚠ DUPLICATES ARE SHOWN BEFORE ANYTHING IS CREATED. The whole point of the
 * link is that one human has one chart; offering "create" without first
 * showing the charts that already match the name and date of birth is how a
 * second one gets made at 7am tomorrow.
 *
 * ⛔ The buttons are a courtesy. The server refuses regardless — see
 * routes/patient_contact_link.js — so a hidden button is never the control.
 */
import React, { useState } from 'react';

const api = () => (typeof window !== 'undefined' ? window.zignagenixAPI : null);

/**
 * One row of a picker — enough to tell two people apart, and nothing more.
 *
 * ⛔⛔ CARD P100c ITEM 2 — THE OLD VERSION COULD NOT. It read only
 * `date_of_birth`, but a CRM CONTACT carries its date of birth as `dob`
 * (database/dobEncryption.js), so every contact rendered as
 *
 *     "Garcia, Maria · DOB —"
 *
 * — and two different Maria Garcias were indistinguishable. Picking the wrong
 * one links a chart to the wrong human, which is the exact harm the link was
 * built to prevent.
 *
 * ⚠ Identifiers only: name, DOB, email, phone, and the record id or MRN.
 * Nothing clinical, and the MRN appears only where the caller already has it.
 */
function personLine(p) {
  const raw = p.dob || p.date_of_birth;
  const dob = raw ? String(raw).slice(0, 10) : '—';
  const bits = [`${p.last_name || ''}, ${p.first_name || ''}`.trim(), `DOB ${dob}`];
  if (p.email) bits.push(p.email);
  if (p.phone) bits.push(p.phone);
  bits.push(p.mrn ? p.mrn : `#${p.id}`);
  return bits.join(' · ');
}

/**
 * Search-and-confirm for one direction of the link.
 *
 * @param {'patient'|'contact'} looking  what the user is searching FOR
 */
export function LinkSearchModal({ looking, patientId, contactId, onDone, onCancel }) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function search() {
    const a = api();
    if (!a || term.trim().length < 2) return;
    setBusy(true); setMsg('');
    try {
      const res = looking === 'patient'
        ? await a.invoke('db:patients:search', term.trim())
        : await a.invoke('crm:contacts:list', { search: term.trim(), limit: 20 });
      const list = Array.isArray(res) ? res : (res && (res.contacts || res.rows)) || [];
      setRows(list.slice(0, 20));
      if (list.length === 0) setMsg('Nothing matched that search.');
    } catch (e) {
      setMsg(`Search failed: ${e && e.message ? e.message : 'unknown error'}`);
    } finally { setBusy(false); }
  }

  async function confirm() {
    const a = api();
    if (!a || !picked) return;
    setBusy(true); setMsg('');
    try {
      const r = await a.invoke('patients:link-contact', {
        patientId: looking === 'patient' ? picked.id : patientId,
        contactId: looking === 'contact' ? picked.id : contactId,
      });
      if (r && r.success) { onDone(r); return; }
      /*
       * ⚠ The server's refusal is shown as it was written. It names the OTHER
       * id and never a name, which is the point — a staff member who may not
       * see that record still learns why the link was refused.
       */
      setMsg((r && r.message) || 'The link was refused.');
    } catch (e) {
      setMsg(`Link failed: ${e && e.message ? e.message : 'unknown error'}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 560 }}>
        <h3>{looking === 'patient' ? 'Link an existing patient record' : 'Link a CRM contact'}</h3>
        {/*
          ⚠ CARD P100c ITEM 2 — THE HINT NOW MATCHES THE SEARCH. It used to
          promise "Name or email" for contacts while crm:contacts:list matches
          first name, last name and organization only, so an email search
          returned nothing and looked like "no such contact".
        */}
        <p style={{ color: '#555', fontSize: 13 }}>
          Search by {looking === 'patient' ? 'name, date of birth or MRN' : 'first or last name'},
          then confirm. One patient record links to one contact.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder={looking === 'patient' ? 'Name, DOB or MRN' : 'Name or email'}
            style={{ flex: 1 }}
          />
          <button className="btn-sm" onClick={search} disabled={busy || term.trim().length < 2}>Search</button>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0', maxHeight: 240, overflowY: 'auto' }}>
          {rows.map((r) => (
            <li key={r.id}>
              <label style={{ display: 'flex', gap: 8, padding: '6px 4px', cursor: 'pointer' }}>
                <input type="radio" name="pick" checked={picked?.id === r.id} onChange={() => setPicked(r)} />
                <span>{personLine(r)}</span>
              </label>
            </li>
          ))}
        </ul>

        {/*
          ⛔ THE CONFIRM STEP NAMES THE RECORD. Two same-named people are the
          whole risk here, so the chosen row is repeated immediately above the
          button that commits it.
        */}
        {picked && (
          <div data-testid="pcl-confirm" style={{
            border: '1px solid #2E8B98', background: '#F0F9FA', borderRadius: 6, padding: 8, fontSize: 13,
          }}>
            Linking: <strong>{personLine(picked)}</strong>
          </div>
        )}
        {msg && <p style={{ color: '#B45309', fontSize: 13 }}>{msg}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-sm btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn-sm" onClick={confirm} disabled={busy || !picked}>Link</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Possible duplicates for a contact about to become a patient.
 *
 * ⛔ SHOWN BEFORE "create", never after. Same last name + DOB, or the same
 * email or phone, is enough to stop and ask — the doctor decides whether this
 * is the same human.
 */
export function DuplicateWarning({ matches, checked, onLinkExisting }) {
  if (!matches || matches.length === 0) return null;
  return (
    <div style={{ border: '1px solid #B45309', background: '#FFFBEB', padding: 10, borderRadius: 6, margin: '8px 0' }}>
      <strong style={{ color: '#B45309' }}>
        {matches.length === 1 ? 'A patient record already looks like this person'
          : `${matches.length} patient records already look like this person`}
      </strong>
      <ul style={{ margin: '6px 0 8px 18px' }}>
        {matches.map((m) => (
          <li key={m.id} style={{ fontSize: 13 }}>
            {personLine(m)}{' '}
            <button className="btn-xs" onClick={() => onLinkExisting(m)}>Link this one instead</button>
          </li>
        ))}
      </ul>
      <span style={{ fontSize: 12, color: '#555' }}>
        Creating a second chart for the same person splits their candidacy and safety screen.
        {/* ⚠ Name what was compared. "No duplicates" means nothing unless the
            reader knows which fields were looked at. */}
        {checked && checked.length > 0 && <> Checked: {checked.join(', ')}.</>}
      </span>
    </div>
  );
}

/**
 * Possible duplicates for a person about to get a chart — Card P100d item 3.
 *
 * ⛔⛔ THE OLD VERSION COULD NOT MATCH AN EMAIL OR A PHONE. It called
 * db:patients:search, which matches first name, last name and MRN only — so
 * "same email" and "same phone" never found anything, and a patient already
 * in the system under a married name or a typo'd surname stayed invisible.
 * A second chart then got created, splitting one person's candidacy and
 * safety screen.
 *
 * ⚠ CHECKED FIRST: patients.email and patients.phone are PLAINTEXT columns
 * (absent from database/encryptedColumns.js, no blind index anywhere), so the
 * server can match them directly and NO schema was added tonight.
 *
 * ⚠ Returns `{ matches, checked }` — `checked` names what was actually
 * compared, so the warning can say so instead of implying completeness.
 */
export async function findPossibleDuplicates({ lastName, dateOfBirth, email, phone }) {
  const a = api();
  if (!a) return { matches: [], checked: [] };
  try {
    const r = await a.invoke('patients:find-duplicates', { lastName, dateOfBirth, email, phone });
    return { matches: (r && r.matches) || [], checked: (r && r.checked) || [] };
  } catch {
    /*
     * ⚠ A failed check must not block the form — but it must not read as
     * "no duplicates" either. An empty `checked` says nothing was compared.
     */
    return { matches: [], checked: [] };
  }
}

/**
 * The key a duplicate result was computed FOR — Card P100d item 2.
 *
 * ⛔ The check used to run ONCE PER PAGE VISIT (`if (createDupes === null)`),
 * so every later create in the same visit skipped it, and editing the name or
 * date of birth after a server error did not re-check. A stale "no
 * duplicates" is worse than none: it is a confident answer about a different
 * person.
 */
export function duplicateKey({ lastName, dateOfBirth, email, phone }) {
  return [lastName, dateOfBirth, email, phone]
    .map((v) => String(v || '').trim().toLowerCase()).join('|');
}

export function CreatePatientFromContact({ contact, onCreated, onLinked, onCancel, onOpenPatient, onDone }) {
  const [form, setForm] = useState({
    firstName: (contact && contact.first_name) || '',
    lastName: (contact && contact.last_name) || '',
    dateOfBirth: (contact && (contact.date_of_birth || contact.dob)) ? String(contact.date_of_birth || contact.dob).slice(0, 10) : '',
    email: (contact && contact.email) || '',
    phone: (contact && contact.phone) || '',
  });
  /*
   * ⚠ The result AND the fields it was computed for — card P100d item 2.
   * A result kept across an edit is an answer about a different person.
   */
  const [dupes, setDupes] = useState(null);      // null = not checked yet
  const [dupeKey, setDupeKey] = useState(null);
  // ⛔ Card H101 item 3 — the flow gave no confirmation at all; the user could
  // not tell whether anything had happened.
  const [created, setCreated] = useState(null);  // { id, mrn } once saved
  const [fieldError, setFieldError] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function check() {
    setBusy(true); setMsg('');
    try {
      setDupes(await findPossibleDuplicates(form));
      setDupeKey(duplicateKey(form));
    } finally { setBusy(false); }
  }

  async function save() {
    const a = api();
    if (!a) return;
    setBusy(true); setMsg('');
    try {
      const r = await a.invoke('db:patients:create', {
        firstName: form.firstName, lastName: form.lastName,
        dateOfBirth: form.dateOfBirth || undefined,
        email: form.email || undefined, phone: form.phone || undefined,
        gender: 'U',
        contactId: contact && contact.id,
      });
      if (r && r.success) {
        // ⛔ Card H101 item 3 — CONFIRM, with the MRN, before anything closes.
        setCreated({ id: r.id, mrn: r.mrn });
        onCreated(r);
        return;
      }
      /*
       * ⛔ THE FORM STAYS OPEN. A refusal (CONTACT_ALREADY_LINKED and friends)
       * means neither a patient nor a link was written — so the doctor's typing
       * must survive it. Closing here would throw the work away on the one
       * path where it is most annoying to redo.
       */
      setMsg((r && r.message) || 'The patient was not created.');
    } catch (e) {
      setMsg(`Create failed: ${e && e.message ? e.message : 'unknown error'}`);
    } finally { setBusy(false); }
  }

  async function linkInstead(patient) {
    const a = api();
    if (!a) return;
    setBusy(true); setMsg('');
    try {
      const r = await a.invoke('patients:link-contact', { patientId: patient.id, contactId: contact.id });
      if (r && r.success) { onLinked(r); return; }
      setMsg((r && r.message) || 'The link was refused.');
    } finally { setBusy(false); }
  }

  /*
   * ⛔⛔ DATE OF BIRTH IS REQUIRED — Card H101 item 2. patients.date_of_birth
   * is NOT NULL with no default, and this form let it be blank: the INSERT
   * failed and Postgres's own words reached the screen. The server now
   * refuses it in plain language too; this stops the round trip.
   */
  const missing = [
    !form.firstName.trim() && 'First name',
    !form.lastName.trim() && 'Last name',
    !form.dateOfBirth && 'Date of birth',
  ].filter(Boolean);
  const ready = missing.length === 0;

  /*
   * ⛔ CARD H101 ITEM 3 — SUCCESS IS A SCREEN, NOT A SILENCE. The flow was
   * "Check for duplicates" then "Create and link" and then nothing visible
   * happened, so the user could not tell whether a chart had been made.
   */
  if (created) {
    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div className="modal" style={{ maxWidth: 560 }}>
          <h3>Patient record created</h3>
          <p data-testid="pcl-created" style={{ color: '#15803D', fontWeight: 600 }}>
            Patient record created and linked — {created.mrn}.
          </p>
          <p style={{ fontSize: 13, color: '#555' }}>
            The physician workflow for this contact now writes to that chart.
          </p>
          {/*
            ⛔⛔ THE PARENT IS TOLD WHEN THE USER LEAVES, NOT WHEN THE SAVE
            LANDS — Card H101b item 1.
            Calling onChanged at save time flipped the header to "linked",
            which made PatientLinkControl take its `link.linked` early return
            and UNMOUNT this very screen. The confirmation Daniel asked for
            never rendered once in the real flow.
          */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              className="btn-sm btn-ghost"
              onClick={() => { if (typeof onDone === 'function') onDone(created); else onCancel(); }}
            >
              Close
            </button>
            {/* ⚠ Uses the SAME hand-off as fix 1, so it opens that exact id. */}
            <button
              className="btn-sm"
              data-testid="pcl-open-chart"
              onClick={() => {
                if (typeof onOpenPatient === 'function') onOpenPatient(created.id);
                if (typeof onDone === 'function') onDone(created);
              }}
            >
              Open this patient&apos;s chart
            </button>
          </div>
        </div>
      </div>
    );
  }

  const checkedThis = dupes !== null && dupeKey === duplicateKey(form);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 560 }}>
        <h3>Create a patient record</h3>
        <p style={{ color: '#555', fontSize: 13 }}>
          Prefilled from this contact. Review every field before saving — a CRM
          contact is a sales record, not a chart.
        </p>

        {[['First name *', 'firstName'], ['Last name *', 'lastName'], ['Date of birth *', 'dateOfBirth'],
          ['Email', 'email'], ['Phone', 'phone']].map(([label, key]) => (
          <label key={key} style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            {label}
            <input
              data-testid={`pcl-${key}`}
              type={key === 'dateOfBirth' ? 'date' : 'text'}
              value={form[key]}
              onChange={set(key)}
              style={{ width: '100%' }}
            />
          </label>
        ))}

        {/* ⛔ Says WHICH field, not "one or more fields failed validation". */}
        {!ready && (
          <p data-testid="pcl-missing" style={{ color: '#B45309', fontSize: 13 }}>
            {missing.join(', ')} {missing.length === 1 ? 'is' : 'are'} required.
          </p>
        )}
        {fieldError && <p style={{ color: '#B45309', fontSize: 13 }}>{fieldError}</p>}

        <DuplicateWarning
          matches={dupes && dupes.matches}
          checked={dupes && dupes.checked}
          onLinkExisting={linkInstead}
        />

        {msg && (
          <p data-testid="pcl-error" style={{ color: '#B45309', fontSize: 13 }}>
            {msg} Nothing was created.
          </p>
        )}

        {/*
          ⛔ CARD H101 ITEM 3 — THE TWO STEPS ARE NUMBERED AND ORDERED.
          "Create and link" stays DISABLED until the check has run for THESE
          field values; editing a field re-arms step 1 (card P100d item 2).
        */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6C757D', marginRight: 'auto' }}>
            {checkedThis ? 'Step 2 of 2' : 'Step 1 of 2'}
          </span>
          <button className="btn-sm btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn-sm"
            data-testid="pcl-check"
            onClick={check}
            disabled={busy || !ready || checkedThis}
          >
            1 · Check for duplicates
          </button>
          <button
            className="btn-sm"
            data-testid="pcl-create"
            onClick={save}
            disabled={busy || !ready || !checkedThis}
          >
            2 · {dupes && dupes.matches.length > 0 ? 'Create new anyway' : 'Create and link'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The one control, used by the contact header AND the Physician tab.
 *
 * ⚠ ONE COMPONENT ON PURPOSE. Two copies of "is this contact linked, and what
 * may this user do about it" drift — this repo has the scar: three copies of
 * the click-to-call flow, two of which silently lost their recovery path.
 *
 * ⛔ `link` is the server's answer: null means NOT YET ASKED and shows
 * neither button, because offering "Create patient record" while the lookup
 * is in flight invites a second chart for someone who already has one.
 */
/**
 * ⛔⛔ WHO MAY UNLINK — ONE DEFINITION, MIRRORING THE SERVER'S OWN LIST.
 *
 * Card P121, from the P118 PR-2 finding: patients:unlink-contact has been
 * admin/sysadmin-only with a required reason since Card P100, and NOTHING in
 * the UI ever called it — so the "reversible by an administrator" half of
 * Carlos's 1A ruling existed on the server and was unreachable from the
 * product. An admin who linked the wrong chart could not undo it.
 *
 * ⚠ THIS IS A ROLE TEST, NOT A PERMISSION TEST, and that is deliberate:
 * routes/patient_contact_link.js:173 refuses on `session.role`, not on a
 * permission. `patients:update` — the secureHandler wrapper's permission — is
 * held by SIX roles (admin, physician, treating_physician,
 * chief_medical_director, nurse, sysadmin), four of which the ruling excludes.
 * Gating this button on the permission would show Unlink to a nurse and a
 * physician and then refuse them at the server: a button that exists to fail.
 *
 * ⚠ THE BUTTON IS A COURTESY, NOT THE CONTROL. The server refuses regardless
 * — held by tests/integration/patientContactLink.test.js, which exercises
 * every role holding patients:update and asserts both the FORBIDDEN code and
 * that the link survived.
 */
export const UNLINK_ROLES = Object.freeze(['admin', 'sysadmin']);

export function canUnlinkPatientContact(user) {
  return UNLINK_ROLES.includes(user && user.role);
}

/**
 * Confirm an unlink, and collect the reason the server already demands.
 *
 * ⚠ NO NEW PHI. The dialog names the MRN only when the caller already had it
 * on screen (it is passed in, never fetched here), and never the patient's
 * name or date of birth. `crm:contact-patient-link` withholds the MRN from
 * callers without patients:read, so a surface that never received one simply
 * says "this patient record".
 *
 * ⚠ The 5-character minimum MIRRORS routes/patient_contact_link.js:179. It is
 * not a second rule: the server refuses a shorter reason with REASON_REQUIRED
 * whatever this form does, and a red arm proves the blank case cannot be
 * submitted here either.
 */
export function UnlinkConfirmModal({ mrn, onCancel, onUnlinked, contactId }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tooShort = reason.trim().length < 5;

  async function submit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (tooShort || busy) return;
    setBusy(true);
    setError('');
    try {
      const a = api();
      if (!a) { setError('Could not reach the server. Nothing was changed.'); setBusy(false); return; }
      const r = await a.invoke('patients:unlink-contact', { contactId, reason: reason.trim() });
      if (r && r.error) {
        // ⚠ Show the server's own message. A generic "failed" would hide
        // FORBIDDEN (wrong role) and REASON_REQUIRED (too short) behind the
        // same shrug, and those need different actions from the user.
        setError(r.message || 'Could not unlink. Please try again.');
        setBusy(false);
        return;
      }
      if (typeof onUnlinked === 'function') onUnlinked(r);
    } catch (err) {
      setError('Could not reach the server. Nothing was changed.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unlink-title"
        className="modal"
        style={{ maxWidth: 460 }}
      >
        <h3 id="unlink-title" style={{ margin: '0 0 10px', fontSize: 16 }}>
          Unlink this chart?
        </h3>

        {/* ⛔ PLAIN WORDS ABOUT WHAT HAPPENS — the card's requirement, and the
            reason it matters: the physician workflow hangs off the CONTACT,
            so unlinking orphans candidacy, the safety screen and proposals. */}
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: '0 0 10px' }}>
          This separates the CRM contact from {mrn ? <strong>patient record {mrn}</strong> : 'this patient record'}.
        </p>
        <ul style={{ fontSize: 12.5, color: '#6C757D', lineHeight: 1.7, margin: '0 0 12px', paddingLeft: 18 }}>
          <li>Neither record is deleted, and no clinical data is removed.</li>
          <li>Candidacy, the safety screen and treatment proposals stay on the contact — but they will no longer point at this chart.</li>
          <li>Copying intake documents to the EMR will refuse again until the contact is linked.</li>
          <li>The reason below is recorded against the contact.</li>
        </ul>

        <label htmlFor="unlink-reason" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
          Why is this link being removed?
        </label>
        <textarea
          id="unlink-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          autoFocus
          placeholder="e.g. linked to the wrong chart"
          style={{ width: '100%', fontSize: 13, padding: 8, borderRadius: 6, border: '1px solid #d1d5db' }}
        />
        <p style={{ fontSize: 11.5, color: tooShort ? '#B45309' : '#6C757D', margin: '4px 0 14px' }}>
          {tooShort ? 'Say a few words — at least 5 characters.' : 'Recorded on the contact timeline.'}
        </p>

        {error && (
          <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', margin: '0 0 10px' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-xs" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="submit"
            className="btn-xs"
            disabled={tooShort || busy}
            style={{ background: tooShort || busy ? '#e5e7eb' : '#DC2626', color: tooShort || busy ? '#9ca3af' : '#fff' }}
          >
            {busy ? 'Unlinking…' : 'Unlink'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** One button, both sides — so the wording cannot drift between them. */
export function UnlinkButton({ onClick }) {
  return (
    <button
      type="button"
      className="btn-xs"
      onClick={onClick}
      title="Separate this contact from its patient record (administrators only)"
      style={{ color: '#B91C1C' }}
    >
      Unlink chart
    </button>
  );
}

/**
 * The patient-side unlink affordance — button plus dialog, in one place.
 *
 * ⛔⛔ THIS EXISTS AS A COMPONENT SO IT CAN BE TESTED, NOT GREPPED. The first
 * version left the button and modal inline in Patients.js and asserted them
 * with a source scan. MEASURED: disabling the control with `{false && …}`
 * left that scan GREEN — the text was still present, and a regex cannot tell
 * a rendered control from a dead one. Rendering this component in a test can.
 *
 * ⚠ It also keeps the two sides from drifting: both now mount the same button
 * and the same dialog, so the wording and the role rule cannot diverge.
 */
export function PatientSideUnlink({ user, linkedContactId, mrn, onUnlinked }) {
  const [open, setOpen] = useState(false);
  if (!linkedContactId || !canUnlinkPatientContact(user)) return null;
  return (
    <>
      <UnlinkButton onClick={() => setOpen(true)} />
      {open && (
        <UnlinkConfirmModal
          mrn={mrn}
          contactId={linkedContactId}
          onCancel={() => setOpen(false)}
          onUnlinked={(r) => { setOpen(false); if (typeof onUnlinked === 'function') onUnlinked(r); }}
        />
      )}
    </>
  );
}

export function PatientLinkControl({ link, contact, canCreatePatient, canLink, canUnlink, onOpenPatient, onChanged }) {
  const [mode, setMode] = useState(null);        // null | 'create' | 'link' | 'unlink'

  if (link === null || link === undefined) {
    return <span style={{ fontSize: 12, color: '#6C757D' }}>Checking patient record…</span>;
  }
  /*
   * ⛔⛔ THE MODAL IS RENDERED BEFORE THE LINKED EARLY-RETURN — Card H101b
   * item 1, and the ordering is the whole bug.
   *
   * The success screen sets the header to "linked", and `link.linked` used
   * to return above this point — unmounting the modal the instant it had
   * something to say. The confirmation never rendered once in the real CRM
   * flow, only in tests that rendered the modal directly.
   */
  if (mode === 'create') {
    return (
      <CreatePatientFromContact
        contact={contact}
        onOpenPatient={onOpenPatient}
        onCancel={() => setMode(null)}
        onCreated={() => { /* ⚠ deliberately silent — see onDone */ }}
        onLinked={(r) => { setMode(null); onChanged({ linked: true, patientId: r.patientId, mrn: r.mrn }); }}
        onDone={(r) => { setMode(null); onChanged({ linked: true, patientId: r.id, mrn: r.mrn }); }}
      />
    );
  }

  if (link.linked) {
    /*
     * ⛔ A RETIRED CHART GETS NO OPEN BUTTON — Card H101b item 3. Offering
     * it would send a clinician into a de-duplicated record; saying so is
     * the only honest option, since this surface cannot know which chart
     * replaced it.
     */
    if (link.inactive) {
      return (
        <>
          <span data-testid="pcl-inactive" style={{ fontSize: 12, color: '#B45309' }}>
            Linked record is inactive{link.mrn ? ` (${link.mrn})` : ''} — search Patients for the active chart.
          </span>
          {/* ⚠ Offered here too: a chart retired by de-duplication is one of
              the likeliest things an administrator needs to unlink. */}
          {canUnlink && <UnlinkButton onClick={() => setMode('unlink')} />}
          {mode === 'unlink' && (
            <UnlinkConfirmModal
              mrn={link.mrn}
              contactId={contact && contact.id}
              onCancel={() => setMode(null)}
              onUnlinked={() => { setMode(null); onChanged({ linked: false }); }}
            />
          )}
        </>
      );
    }
    return (
      <>
        <button
          className="btn-xs"
          title="Open this person's chart"
          onClick={() => { if (typeof onOpenPatient === 'function') onOpenPatient(link.patientId); }}
        >
          Open patient chart{link.mrn ? ` (${link.mrn})` : ''}
        </button>
        {/* ⛔ ADMIN / SYSADMIN ONLY — mirrors the server's own role list.
            See canUnlinkPatientContact above for why this is a role test and
            not a permission test. */}
        {canUnlink && <UnlinkButton onClick={() => setMode('unlink')} />}
        {mode === 'unlink' && (
          <UnlinkConfirmModal
            mrn={link.mrn}
            contactId={contact && contact.id}
            onCancel={() => setMode(null)}
            onUnlinked={() => { setMode(null); onChanged({ linked: false }); }}
          />
        )}
      </>
    );
  }
  return (
    <>
      {canCreatePatient && (
        <button className="btn-xs" onClick={() => setMode('create')}>Create patient record</button>
      )}
      {canLink && (
        <button className="btn-xs" onClick={() => setMode('link')}>Link existing patient</button>
      )}
      {mode === 'link' && (
        <LinkSearchModal
          looking="patient"
          contactId={contact && contact.id}
          onCancel={() => setMode(null)}
          onDone={(r) => { setMode(null); onChanged({ linked: true, patientId: r.patientId, mrn: r.mrn }); }}
        />
      )}
    </>
  );
}

/**
 * Pick a CRM contact to prefill a new patient from — Card P100b item 2.
 *
 * ⛔ A contact ALREADY LINKED is shown disabled with its MRN, so the doctor
 * sees why it cannot be chosen rather than wondering. ⚠ The MRN is only ever
 * what the server chose to return: `crm:contact-patient-link` withholds it
 * from callers without patients:read, so a sales user sees "already linked"
 * and no identifier.
 *
 * ⚠ Rows carry DOB, email, phone and #id — see personLine. Two same-named
 * contacts must be distinguishable before one is picked.
 */
export function ContactPickerModal({ onPick, onCancel }) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [links, setLinks] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function search() {
    const a = api();
    if (!a || term.trim().length < 2) return;
    setBusy(true); setMsg('');
    try {
      const res = await a.invoke('crm:contacts:list', { search: term.trim(), limit: 20 });
      const list = (Array.isArray(res) ? res : (res && (res.contacts || res.rows)) || []).slice(0, 20);
      setRows(list);
      if (list.length === 0) setMsg('Nothing matched that search.');
      // which of them already have a chart?
      const found = {};
      await Promise.all(list.map(async (c) => {
        try { found[c.id] = await a.invoke('crm:contact-patient-link', c.id); } catch { /* unknown */ }
      }));
      setLinks(found);
    } catch (e) {
      setMsg(`Search failed: ${e && e.message ? e.message : 'unknown error'}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 560 }}>
        <h3>Start from a CRM contact</h3>
        <p style={{ color: '#555', fontSize: 13 }}>
          Search by first or last name. Picking one fills this form in — you
          still review every field before saving.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="First or last name"
            style={{ flex: 1 }}
          />
          <button className="btn-sm" onClick={search} disabled={busy || term.trim().length < 2}>Search</button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0', maxHeight: 260, overflowY: 'auto' }}>
          {rows.map((c) => {
            const link = links[c.id];
            const taken = link && link.linked;
            return (
              <li key={c.id} style={{ padding: '6px 4px', opacity: taken ? 0.55 : 1 }}>
                <button
                  className="btn-xs"
                  data-testid={`pcl-contact-${c.id}`}
                  disabled={!!taken}
                  title={taken ? 'This contact already has a patient record' : 'Use this contact'}
                  onClick={() => onPick(c)}
                  style={{ textAlign: 'left', width: '100%' }}
                >
                  {personLine(c)}
                  {taken && (
                    <em style={{ marginLeft: 6, color: '#6C757D' }}>
                      — already linked{link.mrn ? ` to ${link.mrn}` : ''}
                    </em>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {msg && <p style={{ color: '#B45309', fontSize: 13 }}>{msg}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-sm btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default LinkSearchModal;
